import { useMemo } from 'react';
import { DEFAULT_AB_SPLIT_RATIO } from '../constants';
export const getStepTargets = (step) => {
    switch (step.type) {
        case 'check_segment':
            return [
                { target: step.on_true, branch: 'on_true', label: 'on_true' },
                { target: step.on_false, branch: 'on_false', label: 'on_false' }
            ];
        case 'activity':
            return [{ target: step.next, branch: 'next', label: 'next' }];
        case 'ab_split': {
            const ratio = step.ratio ?? DEFAULT_AB_SPLIT_RATIO;
            return [
                { target: step.arms.A, branch: 'armA', label: `arm A (${ratio}%)` },
                { target: step.arms.B, branch: 'armB', label: `arm B (${100 - ratio}%)` }
            ];
        }
    }
};
const findCyclePath = (from, to, stepMap) => {
    const visited = new Set();
    const path = [];
    const dfs = (current) => {
        if (current === from)
            return true;
        if (visited.has(current) || current === 'exit')
            return false;
        visited.add(current);
        const step = stepMap.get(current);
        if (!step)
            return false;
        path.push(step);
        const targets = getStepTargets(step);
        for (const t of targets) {
            if (dfs(t.target))
                return true;
        }
        path.pop();
        visited.delete(current);
        return false;
    };
    const toStep = stepMap.get(to);
    if (!toStep)
        return null;
    path.push(toStep);
    visited.add(to);
    const targets = getStepTargets(toStep);
    for (const t of targets) {
        if (dfs(t.target)) {
            const fromStep = stepMap.get(from);
            if (fromStep)
                path.push(fromStep);
            return path;
        }
    }
    return null;
};
const isLegal = (cyclePath) => cyclePath.some((s) => s.type === 'check_segment' && s.wait_for != null);
export const getReachableStepIds = (steps, entryStepId) => {
    const reachable = new Set();
    if (entryStepId == null)
        return reachable;
    const stepMap = new Map(steps.map((s) => [s.id, s]));
    if (!stepMap.has(entryStepId))
        return reachable;
    const queue = [entryStepId];
    reachable.add(entryStepId);
    while (queue.length > 0) {
        const current = queue.shift();
        const step = stepMap.get(current);
        if (!step)
            continue;
        for (const { target } of getStepTargets(step)) {
            if (target !== 'exit' && stepMap.has(target) && !reachable.has(target)) {
                reachable.add(target);
                queue.push(target);
            }
        }
    }
    return reachable;
};
const useStepGraph = (steps, entryStepId) => {
    return useMemo(() => {
        if (steps.length === 0 || entryStepId == null) {
            return { layers: [], edges: [], unreachable: [] };
        }
        const stepMap = new Map();
        for (const s of steps)
            stepMap.set(s.id, s);
        const distance = new Map();
        const queue = [entryStepId];
        distance.set(entryStepId, 0);
        // Each step is dequeued once, so every (step, branch) edge is emitted
        // once — two branches sharing a target stay two distinct edges.
        const edges = [];
        while (queue.length > 0) {
            const current = queue.shift();
            const step = stepMap.get(current);
            if (!step)
                continue;
            const d = distance.get(current);
            const targets = getStepTargets(step);
            for (const { target, branch, label } of targets) {
                if (target === 'exit')
                    continue;
                if (distance.has(target)) {
                    const cyclePath = findCyclePath(current, target, stepMap);
                    if (cyclePath) {
                        const legal = isLegal(cyclePath);
                        edges.push({
                            from: current,
                            to: target,
                            branch,
                            label,
                            isLegalCycle: legal,
                            isIllegalCycle: !legal,
                            cyclePath: legal ? cyclePath.map((s) => s.id) : undefined
                        });
                    }
                    else {
                        edges.push({
                            from: current,
                            to: target,
                            branch,
                            label,
                            isLegalCycle: false,
                            isIllegalCycle: false
                        });
                    }
                    continue;
                }
                distance.set(target, d + 1);
                queue.push(target);
                edges.push({
                    from: current,
                    to: target,
                    branch,
                    label,
                    isLegalCycle: false,
                    isIllegalCycle: false
                });
            }
        }
        const maxLayer = Math.max(0, ...Array.from(distance.values()));
        const layers = Array.from({ length: maxLayer + 1 }, () => []);
        for (const [id, d] of distance) {
            const step = stepMap.get(id);
            if (step)
                layers[d].push(step);
        }
        const unreachable = steps.filter((s) => !distance.has(s.id));
        return { layers, edges, unreachable };
    }, [steps, entryStepId]);
};
export default useStepGraph;
